"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TickerWebsocket = void 0;
const rxjs_1 = require("rxjs");
const trade_utils_lib_1 = require("trade_utils_lib");
const CacheData_1 = require("./base/CacheData");
const SubsPairMgr_1 = require("./base/SubsPairMgr");
const BaseWebSocket_1 = require("./BaseWebSocket");
const QuotationSocketMgr_1 = require("./QuotationSocketMgr");
function getCacheKey(ticker) {
    return `${ticker.exchange}/${ticker.base}/${ticker.quote}`;
}
class TickerWebsocket extends BaseWebSocket_1.BaseWebSocket {
    constructor() {
        super(...arguments);
        this.dataCache = new CacheData_1.CacheData(new Map(), new rxjs_1.BehaviorSubject(new Map()));
        this.tickerPairPoolMgr = new SubsPairMgr_1.SubsPairMgr(QuotationSocketMgr_1.SubscribeEventType.ticker, (type, param) => {
            return this.send(QuotationSocketMgr_1.SubscribeEventType.ticker, param);
        });
    }
    handleTickerData(pushData) {
        if (!pushData || pushData.length === 0) {
            return;
        }
        pushData.forEach((tickerInfo) => {
            const ticker = TickerWebsocket.parsePushData(tickerInfo);
            const oldTicker = this.dataCache.getCacheData().get(getCacheKey(ticker));
            TickerWebsocket.setTickerPriceUp(ticker, oldTicker);
            this.dataCache.getCacheData().set(getCacheKey(ticker), ticker);
        });
        this.dataCache.emit();
    }
    static setTickerPriceUp(ticker, oldTicker) {
        if (oldTicker) {
            ticker.prePrice = oldTicker.latest;
            if (ticker.latest === oldTicker.latest) {
                ticker.isPriceUp = oldTicker.isPriceUp == undefined || oldTicker.isPriceUp;
            }
            else {
                ticker.isPriceUp = ticker.latest > oldTicker.latest;
            }
        }
        else {
            ticker.isPriceUp = true;
        }
    }
    static parsePushData(tickerInfo) {
        return {
            exchange: tickerInfo.e,
            fullName: tickerInfo.n,
            symbol: tickerInfo.b + "/" + tickerInfo.q,
            base: tickerInfo.b,
            quote: tickerInfo.q,
            latest: Number(tickerInfo.p),
            fiat: Number(tickerInfo.u),
            latestAmount: 0,
            changePercent: Number(tickerInfo.f || tickerInfo.c),
            prev24HPrice: Number(tickerInfo.p) - Number(tickerInfo.c) * Number(tickerInfo.p),
            high: Number(tickerInfo.h),
            low: Number(tickerInfo.l),
            baseVol: Number(tickerInfo.v),
            quoteVol: Number(tickerInfo.w),
        };
    }
    /**
     * 订阅整个交易所的行情
     * @param exchanges
     * key 为 exchange/base/quote
     */
    getTickerObservableByExs(exchanges) {
        const pairs = exchanges.map((market) => ({ market, base: "", quote: "" }));
        this.tickerPairPoolMgr.addSubscribePair(pairs);
        return new rxjs_1.Observable((observer) => {
            const subscription = this.dataCache.getDataSubject().subscribe((result) => {
                const tickerMap = new Map();
                for (const item of result.values()) {
                    if (item.exchange && exchanges.includes(item.exchange)) {
                        tickerMap.set(getCacheKey(item), item);
                    }
                }
                observer.next(tickerMap);
            }, (err) => {
                (0, trade_utils_lib_1.getTradeDependencyInstance)().log(trade_utils_lib_1.LogLevel.LEVEL_ERROR, QuotationSocketMgr_1.QuotationSocketMgr.TAG, `getTickerObservableByExs ${JSON.stringify(exchanges)} error ${JSON.stringify(err)}`);
            });
            observer.add(() => {
                this.tickerPairPoolMgr.removeSubscribePair(pairs);
                subscription.unsubscribe();
            });
        });
    }
    /**
     * 订阅交易对价格
     * @param exchange
     * key 为 exchange/base/quote
     */
    getTickerObservableByEx(exchange) {
        return this.getTickerObservableByExs([exchange]);
    }
    /**
     * 订阅交易对价格
     * @param exchange
     * key 为 base/quote
     * @deprecated 使用getTickerObservableByEx, 取值时key变为exchange/base/quote
     */
    getTickerObservable(exchange) {
        const pairs = [{ market: exchange, base: "", quote: "" }];
        this.tickerPairPoolMgr.addSubscribePair(pairs);
        return new rxjs_1.Observable((observer) => {
            const subscription = this.dataCache.getDataSubject().subscribe((result) => {
                const tickerMap = new Map();
                for (const item of result.values()) {
                    if (item.exchange && exchange === item.exchange) {
                        tickerMap.set(`${item.base}/${item.quote}`, item);
                    }
                }
                observer.next(tickerMap);
            }, (err) => {
                (0, trade_utils_lib_1.getTradeDependencyInstance)().log(trade_utils_lib_1.LogLevel.LEVEL_ERROR, QuotationSocketMgr_1.QuotationSocketMgr.TAG, `getTickerObservable ${exchange} error ${JSON.stringify(err)}`);
            });
            observer.add(() => {
                this.tickerPairPoolMgr.removeSubscribePair(pairs);
                subscription.unsubscribe();
            });
        });
    }
    getTickerObservableByPair(exchange, base, quote) {
        this.tickerPairPoolMgr.addSubscribePair([{ market: exchange, base: base, quote: quote }]);
        return new rxjs_1.Observable((observer) => {
            const subscription = this.dataCache.getDataSubject().subscribe((result) => {
                const findResult = result.get(`${exchange}/${base}/${quote}`);
                if (findResult) {
                    observer.next(findResult);
                }
            }, (err) => { });
            observer.add(() => {
                subscription.unsubscribe();
                this.tickerPairPoolMgr.removeSubscribePair([
                    {
                        market: exchange,
                        base: base,
                        quote: quote,
                    },
                ]);
            });
        });
    }
    getTickerObservableBatch(pairInfoList) {
        this.tickerPairPoolMgr.addSubscribePair(pairInfoList);
        return new rxjs_1.Observable((observer) => {
            const subscription = this.dataCache.getDataSubject().subscribe((result) => {
                const filterResult = [];
                pairInfoList.forEach((pairInfo) => {
                    const ticker = result.get(`${pairInfo.market}/${pairInfo.base}/${pairInfo.quote}`);
                    if (ticker) {
                        filterResult.push(ticker);
                    }
                });
                observer.next(filterResult);
            }, (error) => {
                (0, trade_utils_lib_1.getTradeDependencyInstance)().log(trade_utils_lib_1.LogLevel.LEVEL_ERROR, QuotationSocketMgr_1.QuotationSocketMgr.TAG, `getTickerObservableBatch error ${JSON.stringify(error)}`);
            });
            observer.add(() => {
                subscription.unsubscribe();
                this.tickerPairPoolMgr.removeSubscribePair(pairInfoList);
            });
        });
    }
    initTickerCache() {
        const key = "pionex_cached_ticker_key";
        (0, trade_utils_lib_1.getTradeDependencyInstance)()
            .getLocalStorage()
            .getItem(key)
            .then((result) => {
            if (!result) {
                return;
            }
            let tickerList;
            try {
                tickerList = JSON.parse(result);
            }
            catch (e) { }
            if (tickerList && tickerList.length > 0) {
                const map = new Map();
                tickerList.forEach((item) => {
                    map.set(getCacheKey(item), item);
                });
                this.dataCache.updateCache(map);
            }
            (0, trade_utils_lib_1.getTradeDependencyInstance)().log(trade_utils_lib_1.LogLevel.LEVEL_INFO, QuotationSocketMgr_1.QuotationSocketMgr.TAG, `init ticker cache ${Date.now()}`);
        })
            .catch((error) => { });
    }
    onAppPause() {
        this.recordTickerMap();
    }
    recordTickerMap(spot) {
        const key = "pionex_cached_ticker_key";
        const cacheMap = this.dataCache.getCacheData();
        try {
            const tickerList = Array.from(cacheMap.values());
            if (tickerList.length > 0) {
                (0, trade_utils_lib_1.getTradeDependencyInstance)()
                    .getLocalStorage()
                    .setItem(key, JSON.stringify(tickerList))
                    .then(() => { })
                    .catch((err) => { });
            }
        }
        catch (e) { }
    }
    initTickerCachePrepare(disableCache) {
        if (!disableCache) {
            this.initTickerCache();
        }
    }
}
exports.TickerWebsocket = TickerWebsocket;
